/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from "@emotion/core";
import type { State } from "@popperjs/core";
import { ThemeKey } from "@smartsheet/design-tokens";
import { PropGetters } from "downshift";
import maxSize from "popper-max-size-modifier";
import { Fragment, HTMLAttributes, Key, RefAttributes, useCallback, useEffect, useMemo, useRef } from "react";
import { VariableSizeList } from "react-window";
import { useI18n } from "../../../../i18n/i18n";
import { useTheme } from "../../../../theme/utils/useTheme";
import { Flyout, FlyoutPlacement, FlyoutProps } from "../../../flyout";
import { getMenuAnimation } from "../../../menu/styled/getMenuAnimation";
import { menuTheme } from "../../../menu/styled/menuTheme";
import { DEFAULT_MAX_MENU_HEIGHT } from "../../constants";
import { BaseOptionType, FormatOption, OptionSpreadProps } from "../../types";
import {
    isSelectAllMarkerOption,
    selectAllMarkerOption,
    stopPropagationHandler,
    isCreatableMarkerOption,
    creatableMarkerOption,
    isSelectV2OptionSeparator,
    selectV2OptionSeparator,
} from "../../utils";
import { CreatableOption } from "../CreatableOption";
import { SelectAllOption } from "../multiselect/SelectAllOption";
import { selectV2Theme } from "../selectV2Theme";
import { NoResultsMessage } from "../shared/NoResultsMessage";
import { LoadingOption } from "./LoadingOption";
import { VariableSizeListStyled } from "./VariableSizeListStyled";
import { VirtualizedOption } from "./VirtualizedOption";
import { VirtualizedSeparatorStyled } from "./VirtualizedSeparatorStyled";

interface SelectMenuProps<Option extends BaseOptionType> {
    /**
     * Only applicable to multiselects.
     */
    allSelected: boolean;
    /**
     * Only applicable to multiselects.
     */
    enableSelectAll: boolean;

    flyoutProps: FlyoutProps;
    /**
     * This is the formatOption from the user, or the default
     */
    formatOption: FormatOption<Option>;
    getItemProps: (
        ...args: Parameters<PropGetters<Option>["getItemProps"]>
    ) => HTMLAttributes<HTMLDivElement> & RefAttributes<HTMLDivElement>;
    getMenuProps: (
        ...args: Parameters<PropGetters<Option>["getMenuProps"]>
    ) => HTMLAttributes<HTMLDivElement> & RefAttributes<HTMLDivElement>;
    /**
     * Used to provide a deterministic way to identify each item in the following cases:
     * - formatOption not provided so default formatting will apply
     * - virtualization is on, each item will have a key based on this function
     * - custom option formatting, key will be passed up through params, a custom key could be used here
     */
    getOptionKey: (option: Option) => Key;
    highlightedIndex: number;
    /**
     * Only applicable to comboboxes.
     */
    inputValue: string;
    isLoading: boolean;
    isOpen: boolean;
    maxMenuHeight: number;
    maxOptionsUntilVirtualization: number;
    // Can we make this reference their actual types? Right now that'd be a circular ref.
    optionToString: (option: Option) => string;
    options: Option[];
    /**
     * Only applicable to multiselects.
     */
    selectedItems: Option[];
    targetWidth: number;

    /**
     * extra props to be spread on the flyout component
     */
    additionalSelectFlyoutProps?: HTMLAttributes<HTMLDivElement> | FlyoutProps;

    className?: string;

    extraMenuProps?: HTMLAttributes<HTMLDivElement>;

    isOptionDisabled?: (option: Option) => boolean;

    menuWidth?: "match-control" | "auto" | "auto-grow" | "auto-shrink";
    optionHeight?: number;
    selectedOption?: Option | null;
}

export interface PopperModifierState {
    state: State;
}

export const SelectMenu = <Option extends BaseOptionType>({
    extraMenuProps,
    flyoutProps,
    formatOption,
    getItemProps,
    getMenuProps,
    highlightedIndex,
    isLoading,
    isOpen,
    isOptionDisabled,
    menuWidth,
    maxMenuHeight,
    maxOptionsUntilVirtualization,
    optionHeight: providedOptionHeight,
    options,
    targetWidth,
    allSelected,
    enableSelectAll,
    inputValue,
    selectedItems,
    selectedOption,
    getOptionKey,
    className,
    additionalSelectFlyoutProps,
}: SelectMenuProps<Option>) => {
    const { i18n } = useI18n();
    const { key } = useTheme();
    const themeMode = key as ThemeKey;
    const selectAllOptionHeight = menuTheme.optionHeight.normal(themeMode);
    const optionHeight = providedOptionHeight || menuTheme.optionHeight.normal(themeMode);
    const separatorHeight = menuTheme.separatorHeight(themeMode);
    const showAsListbox = isOpen && options.length > 0;
    const shouldVirtualize = options.length > maxOptionsUntilVirtualization;
    const listRef = useRef<VariableSizeList>(null);

    const totalSelectedItems: number = selectedItems.length;
    //menu options without the select all, separator markers and creatable marker
    const regularOptions = options.filter((option) => option.value !== undefined && option.label !== undefined);
    const totalMenuItems: number = regularOptions.length;

    const {
        // Pull this off - we'll reattach our own later.
        "aria-labelledby": _unused,
        ...restMenuProps
    } = getMenuProps(
        {
            "aria-label": showAsListbox ? i18n._(/*i18n*/ "listbox contents") : undefined,
            id: flyoutProps.id,
            // We have to turn off the listbox role when it doesn't have any children. This is
            // from the spec - a listbox MUST have children of the role "option" | "group".
            role: showAsListbox ? "listbox" : "none",
            ...extraMenuProps,
        },
        {
            // The Flyout doesn't attach itself until a tick later. Trust us downshift, we're good :)
            suppressRefError: true,
        }
    );

    const isOptionSelected = useCallback(
        (option: Option) => {
            return selectedOption === option || selectedItems.includes(option);
        },
        [selectedItems, selectedOption]
    );

    const getRowSize = (index: number) => {
        if (isSelectAllMarkerOption(options[index])) {
            return selectAllOptionHeight + separatorHeight;
        } else if (isSelectV2OptionSeparator(options[index])) {
            return separatorHeight;
        }
        return optionHeight;
    };

    const getRenderedOption: FormatOption<Option> = useCallback(
        (option, index, highlightedIndex, extraProps) => {
            const isSelectAllOption = isSelectAllMarkerOption(option);
            const optionProps = {
                "data-highlighted": highlightedIndex === index ? "true" : "false",
                "aria-disabled": isOptionDisabled?.(option) ?? false,
                role: "option",
            } as const;
            if (enableSelectAll && isSelectAllOption) {
                //destructuring to remove the role and aria-selected from the select all option to avoid screen reader reading selected/not-selected for the select all option
                const { role: _unusedRole, "aria-selected": _unusedAriaSelected, ...propsWithoutHeight } = extraProps;
                delete propsWithoutHeight.style?.height;

                return (
                    <Fragment key={selectAllMarkerOption.value}>
                        <SelectAllOption
                            totalSelectedItems={totalSelectedItems}
                            totalOptions={totalMenuItems}
                            checkedState={
                                selectedItems.length === 0 ? "unchecked" : allSelected ? "checked" : "indeterminate"
                            }
                            {...propsWithoutHeight}
                            {...optionProps}
                        />
                        <VirtualizedSeparatorStyled
                            shouldVirtualize={shouldVirtualize}
                            optionHeight={selectAllOptionHeight}
                        />
                    </Fragment>
                );
            } else if (isCreatableMarkerOption(option)) {
                return (
                    <CreatableOption
                        inputValue={inputValue}
                        optionHeight={optionHeight}
                        {...extraProps}
                        key={creatableMarkerOption.value}
                        aria-selected="false"
                        {...optionProps}
                    />
                );
            } else if (isSelectV2OptionSeparator(option)) {
                return (
                    <VirtualizedSeparatorStyled
                        shouldVirtualize={shouldVirtualize}
                        optionHeight={separatorHeight}
                        top={extraProps.style?.top}
                        key={`${selectV2OptionSeparator.value}-${index}`}
                    />
                );
            } else {
                return formatOption(
                    option,
                    index,
                    highlightedIndex,
                    { ...extraProps, ...optionProps },
                    isOptionSelected(option),
                    {
                        inputValue,
                        isDisabled: isOptionDisabled?.(option) ?? false,
                    }
                );
            }
        },
        [
            allSelected,
            enableSelectAll,
            formatOption,
            inputValue,
            isOptionSelected,
            isOptionDisabled,
            optionHeight,
            selectAllOptionHeight,
            selectedItems.length,
            separatorHeight,
            shouldVirtualize,
            totalSelectedItems,
            totalMenuItems,
        ]
    );

    const itemData = useMemo(
        () => ({
            getItemProps,
            getRenderedOption,
            highlightedIndex,
            options,
            getOptionKey,
            isOptionSelected,
            metadata: { inputValue },
        }),
        [getItemProps, getOptionKey, getRenderedOption, highlightedIndex, isOptionSelected, options, inputValue]
    );

    const itemKey = useCallback(
        (index: number) => {
            const option = options[index];
            if (isSelectV2OptionSeparator(option)) return `${option.value}-${index}`;
            if (isSelectAllMarkerOption(option) || isCreatableMarkerOption(option)) return option.value;
            if (isLoading && index === options.length) return "loading-indicator";
            return getOptionKey(option);
        },
        [getOptionKey, options, isLoading]
    );

    useEffect(() => {
        listRef.current?.resetAfterIndex(0);
    }, [options]);

    useEffect(() => {
        if (highlightedIndex >= 0) listRef.current?.scrollToItem(highlightedIndex);
    }, [highlightedIndex]);

    // increase item count by one if loading to allow for the loading indicator to be rendered
    const itemCount = isLoading ? options.length + 1 : options.length;

    const menu = isOpen ? (
        <Fragment>
            {shouldVirtualize ? (
                <VariableSizeListStyled
                    height={maxMenuHeight}
                    width="100%"
                    itemCount={itemCount}
                    itemSize={getRowSize}
                    itemKey={itemKey}
                    ref={listRef}
                    itemData={itemData}
                >
                    {VirtualizedOption}
                </VariableSizeListStyled>
            ) : (
                <Fragment>
                    {options.map((option, index) => {
                        const isSelected = isOptionSelected(option);
                        return getRenderedOption(
                            option,
                            index,
                            highlightedIndex,
                            {
                                ...(getItemProps({
                                    item: option,
                                    index,
                                    onClick: stopPropagationHandler,
                                    key: getOptionKey(option),
                                }) as OptionSpreadProps),
                                // Override this for wai-aria 1.2 support. Downshift assumes that
                                // selection follows focus and the last focused item in the dropdown
                                // will be selected. This is not the case for us, so we need to
                                // attach this attribute ourselves, otherwise screen readers (esp.
                                // VoiceOver) will assume all items are selected.
                                "aria-selected": isSelected ? "true" : "false",
                            },
                            isSelected,
                            {
                                inputValue,
                                isDisabled: isOptionDisabled?.(option) ?? false,
                            }
                        );
                    })}
                    {isLoading && <LoadingOption />}
                </Fragment>
            )}
            {!isLoading && (options.length === 0 || (options.length === 1 && isSelectAllMarkerOption(options[0]))) && (
                <NoResultsMessage height={optionHeight} themeMode={themeMode} />
            )}
        </Fragment>
    ) : null;

    // when menuWidth options were introduced, this became
    // necessary in order to not break the existing placement API
    const getMenuPlacement = (placement: FlyoutPlacement | undefined) => {
        switch (placement) {
            case "bottom":
                return "bottom-start";
            case "top":
                return "top-start";
            default:
                return "auto";
        }
    };
    //https://www.npmjs.com/package/popper-max-size-modifier?activeTab=readme
    //https://popper.js.org/react-popper/v2/faq/#why-i-get-render-loop-whenever-i-put-a-function-inside-the-popper-configuration
    const applyMaxSize = useMemo(
        () => ({
            name: "applyMaxSize",
            enabled: true,
            phase: "main" as const,
            requires: ["maxSize"],
            //eslint-disable-next-line @typescript-eslint/no-explicit-any
            fn: ({ state }: PopperModifierState) => {
                if (maxMenuHeight === DEFAULT_MAX_MENU_HEIGHT) return;
                // The `maxSize` modifier provides this data
                const { height } = state.modifiersData.maxSize as { height: number };
                const newHeight = Math.min(maxMenuHeight, height) <= DEFAULT_MAX_MENU_HEIGHT ? maxMenuHeight : height;
                state.styles.popper = {
                    ...state.styles.popper,
                    maxHeight: `${newHeight}px`,
                };
            },
        }),
        [maxMenuHeight]
    );

    return (
        <Flyout
            {...flyoutProps}
            {...restMenuProps}
            className={className}
            placement={getMenuPlacement(flyoutProps?.placement)}
            style={{
                ...restMenuProps.style,
                width: menuWidth === "match-control" ? `${targetWidth}px` : `unset`,
                minWidth: menuWidth === "auto-grow" ? `${targetWidth}px` : `unset`,
                maxWidth: menuWidth === "auto-shrink" ? `${targetWidth}px` : `unset`,
                opacity: isOpen ? 1 : 0,
            }}
            popperProps={{
                modifiers: [maxSize, applyMaxSize],
            }}
            css={
                isOpen
                    ? css`
                          padding: 8px 0;
                          overflow-y: auto;
                          overflow-x: hidden;
                          background-color: ${menuTheme.backgroundColor.rest(themeMode)};
                          box-shadow: ${menuTheme.boxShadow(themeMode)};
                          border-radius: ${menuTheme.borderRadius(themeMode)};
                          max-height: ${maxMenuHeight}px;
                          box-sizing: border-box;
                          outline: none;
                          display: flex;
                          flex-direction: column;
                          gap: ${selectV2Theme.option.gap(themeMode)};
                          transition: opacity ${menuTheme.motion.animation(themeMode)};
                          &[data-popper-placement="top-start"] {
                              animation: ${getMenuAnimation("top")} ${menuTheme.motion.animation(themeMode)};
                          }
                          &[data-popper-placement="bottom-start"] {
                              animation: ${getMenuAnimation("bottom")} ${menuTheme.motion.animation(themeMode)};
                          }

                          ${shouldVirtualize
                              ? `
                              padding: 0;
                              `
                              : `
                              `}
                      `
                    : undefined
            }
            {...additionalSelectFlyoutProps}
        >
            {menu}
        </Flyout>
    );
};
